import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { useAuthStore } from '@/features/auth'

const API_BASE_URL = import.meta.env.DEV ? 'http://localhost:8000/api' : '/api'

/**
 * Error thrown by the API client. Carries the HTTP status so callers can
 * branch on it (e.g. 401 → wrong password, 409 → conflict) — the raw axios
 * error is not propagated past the response interceptor.
 */
export class ApiError extends Error {
    status?: number
    constructor(message: string, status?: number) {
        super(message)
        this.name = 'ApiError'
        this.status = status
    }
}

/**
 * Extracts a human-readable error message from any thrown value.
 * Use this when displaying API errors in the UI or toasts.
 */
export const extractApiError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data
        if (typeof data === 'object' && data !== null) {
            // FastAPI returns { detail: ... }; some endpoints use { message: ... }.
            if ('detail' in data && typeof (data as { detail: unknown }).detail === 'string') {
                return String((data as { detail: unknown }).detail)
            }
            if ('message' in data) return String((data as { message: unknown }).message)
        }
        if (typeof data === 'string' && data.length > 0) return data
        if (error.code === 'ECONNABORTED') return 'Request timed out'
        if (!error.response) return 'Network error — check your connection'
        return error.message
    }
    if (error instanceof Error) return error.message
    return 'An unexpected error occurred'
}

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: { 'Content-Type': 'application/json' },
})

// Inject Bearer token from auth store on every request
api.interceptors.request.use(
    (config) => {
        const token = useAuthStore.getState().getToken()
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => Promise.reject(error),
)

// On 401, trigger logout; normalize all errors to ApiError (preserving status).
// The password-change endpoint returns 401 to mean "wrong current password",
// not an expired session, so it must NOT trigger a logout.
const SKIP_AUTO_LOGOUT = ['/users/me/password']

api.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined
        if (status === 401) {
            const url = axios.isAxiosError(error) ? (error.config?.url ?? '') : ''
            if (!SKIP_AUTO_LOGOUT.some((path) => url.includes(path))) {
                useAuthStore.getState().logout()
            }
        }
        return Promise.reject(new ApiError(extractApiError(error), status))
    },
)

export const apiService = {
    get:    <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.get<T>(url, config),
    post:   <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.post<T>(url, data, config),
    put:    <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.put<T>(url, data, config),
    delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.delete<T>(url, config),
    patch:  <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => api.patch<T>(url, data, config),
}

export default api
